import { Modal, ModalBody, ModalCloseButton, ModalContent, ModalOverlay } from '@chakra-ui/react';
import { StandardSnack } from '@components/snackbar';
import {
  CustomDateIcon,
  CustomEmailIcon,
  CustomMultiselectIcon,
  CustomNumberIcon,
  CustomPersonIcon,
  CustomPhoneIcon,
  CustomSelectIcon,
  CustomTextIcon,
  CustomUrlIcon,
  SearchIcon,
  TemplateTaskIcon,
} from '@sketchnote/icons';
import { Input, LoaderXs } from '@sketchnote/shared-components';
import cn from 'classnames';
import debounce from 'lodash/debounce';
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { API } from 'src/api';

import { useTasksStore } from '../store/useTasksStore';
import { TaskT } from '../types/tasks';

export const TaskTemplateImport = ({
  task,
  importTemplate,
  setImportTemplate,
}: {
  task: TaskT;
  importTemplate: boolean;
  setImportTemplate: Dispatch<SetStateAction<boolean>>;
}) => {
  const [page, setPage] = useState(1);
  const [searchValue, setSearchValue] = useState('');
  const [hasMore, setHasMore] = useState(false);
  const [searchFlag, setSearchFlag] = useState(0);
  const [taskTemplates, setTaskTemplates] = useState<TaskT[]>([]);
  const [imported, setImported] = useState('');
  // const { onImportTemplate } = useTasksStore();

  const getAllTaskTemplates = async (dependencyFlag: boolean) => {
    let query = `/board/task/template?page=${page}&limit=10`;
    searchValue && (query += `&search=${searchValue}`);
    const response = await API.get(query);
    const { docs, hasNextPage } = formatTemplate(response.data.data);
    setHasMore(hasNextPage);

    if (dependencyFlag) setTaskTemplates([...docs]);
    else {
      setTaskTemplates([...taskTemplates, ...docs]);
      // setTaskTemplates(docs);
    }
  };
  useEffect(() => {
    getAllTaskTemplates(false);
  }, [page]);

  useEffect(() => {
    getAllTaskTemplates(true);
  }, [searchFlag]);

  const debounceSearch = useCallback(
    debounce(() => {
      setSearchFlag(Date.now());
    }, 2000),
    []
  );
  const handleOnChange = (e) => {
    setSearchValue(e.target.value);
    debounceSearch();
  };

  const handleFetchTaskTemplates = () => {
    setPage(page + 1);
  };

  const handleImport = (template) => {
    // task.id && onImportTemplate(template, task.id);
    setImportTemplate(false);
  };

  const formatTemplate = (data) => {
    const docs = data.docs.map((item) => {
      item = { id: item._id, ...item };
      item.fields = item.fields.map((field) => {
        return { id: field._id, ...field };
      });
      return item;
    });
    const hasNextPage = data.docs.hasNextPage;
    return { docs, hasNextPage };
  };

  return (
    <Modal isOpen={importTemplate} onClose={() => setImportTemplate(false)}>
      <ModalOverlay />
      <ModalContent
        maxWidth="42rem"
        maxHeight="48.2rem"
        borderRadius="0.8rem"
        className="bg-white static rounded-lg"
        margin="auto"
      >
        <ModalCloseButton />
        <div className="p-28">
          <div>
            <p className="font-medium text-lg text-gray-999">📥 Import a template</p>
            {!searchValue && taskTemplates?.length === 0 ? (
              <div className="mt-14 mb-10">
                <EmptyState contents={['Create task templates to fill up this area!']} />
              </div>
            ) : (
              <Input
                placeholder="Search for template"
                inputClassName="placeholder-gray-500 font-regular text-sm"
                field="search"
                value={searchValue}
                leftIcon={<SearchIcon wrapperClass="align-text-bottom" />}
                wrapperClassName="py-2 bg-gray-100 border border-none h-40 mt-18 mb-10"
                onChange={handleOnChange}
              />
            )}
          </div>
          <div
            className={cn('max-h-219 min-h-80 overflow-y-auto', {
              'h-482': taskTemplates?.length > 0,
              'h-80': taskTemplates?.length <= 0,
            })}
            id="scrollableDiv"
          >
            <InfiniteScroll
              dataLength={taskTemplates.length}
              next={handleFetchTaskTemplates}
              hasMore={hasMore}
              scrollableTarget="scrollableDiv"
              loader={
                <div>
                  <LoaderXs className="mx-auto text-xss m-10" />
                </div>
              }
              endMessage={
                <div className="flex items-center justify-center pt-10 pb-10">
                  <StandardSnack text="That’s all, folks!" />
                </div>
              }
            >
              {taskTemplates &&
                taskTemplates.map((template, index) => {
                  return (
                    <div
                      key={index}
                      className="border-b"
                      role="button"
                      onClick={() => handleImport(template)}
                    >
                      <div className="py-2">
                        <span className="flex mt-12 items-center" key={index}>
                          <TemplateTaskIcon wrapperClass="text-gray-400" />
                          <span
                            className="text-gray-800 ml-8 font-medium text-base truncate"
                            onClick={(e) => {
                              template?.name && setImported(template?.name.toString());
                            }}
                          >
                            {template?.name && template?.name?.toString()}
                          </span>
                        </span>
                      </div>
                      <div className="flex items-center mt-4 mb-16">
                        {template.fields?.slice(0, 3).map((field, index) => {
                          return (
                            <span className="flex mr-6 items-center" key={index}>
                              {field.type === 'text' ? (
                                <CustomTextIcon className="text-gray-400" />
                              ) : field.type === 'number' ? (
                                <CustomNumberIcon className="text-gray-400" />
                              ) : field.type === 'date' ? (
                                <CustomDateIcon className="text-gray-400" />
                              ) : field.type === 'select' ? (
                                <CustomSelectIcon className="text-gray-400" />
                              ) : field.type === 'multiselect' ? (
                                <CustomMultiselectIcon className="text-gray-400" />
                              ) : field.type === 'url' ? (
                                <CustomUrlIcon className="text-gray-400" />
                              ) : field.type === 'email' ? (
                                <CustomEmailIcon className="text-gray-400" />
                              ) : field.type === 'phone' ? (
                                <CustomPhoneIcon className="text-gray-400" />
                              ) : field.type === 'person' ? (
                                <CustomPersonIcon className="text-gray-400" />
                              ) : null}

                              <span className="font-regular text-gray-600 text-xs ml-4 truncate">
                                {field.label}
                              </span>
                            </span>
                          );
                        })}
                        {template?.fields && template?.fields?.length > 3 ? (
                          <div className="flex ml-4 h-18 text-xs items-center ">
                            + {template.fields?.length - 3}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  );
                })}
            </InfiniteScroll>
          </div>
        </div>
      </ModalContent>
    </Modal>
  );
};

const EmptyState = ({ contents }: { contents: string[] }) => (
  <div className="flex justify-center text-sm text-center p-6 text-gray-999 w-full bg-gray-100 border-gray-200 rounded border">
    <div className="py-5">
      {contents.map((obj, index) => (
        <p key={`${obj}${index}`}>{obj}</p>
      ))}
    </div>
  </div>
);
